import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Loader2, Upload, FileText, X, Plus, AlertCircle } from 'lucide-react';
import { SWISS_CANTONS } from '@bettermatch/shared';
import { api } from '../lib/api';
import { PostalCodeAutocomplete } from '../components/PostalCodeAutocomplete';
import { PageHeader } from '../components/ui';
import { toast } from '../stores/toast-store';

interface UploadedFile {
  file: File;
  id: string;
}

interface FormErrors {
  title?: string;
  condition?: string;
  age?: string;
  gender?: string;
  postalCode?: string;
}

export function NewCase() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    title: '',
    condition: '',
    patientInfo: {
      age: undefined as number | undefined,
      gender: '' as '' | 'male' | 'female' | 'other',
      canton: '',
      postalCode: '',
    },
  });
  const [files, setFiles] = useState<UploadedFile[]>([]);

  // Validation logic
  const getErrors = (): FormErrors => {
    const errors: FormErrors = {};
    if (!formData.title.trim()) {
      errors.title = 'Case title is required';
    }
    if (!formData.condition.trim()) {
      errors.condition = 'Medical condition is required';
    } else if (formData.condition.trim().length < 10) {
      errors.condition = 'Please provide at least 10 characters';
    }
    if (formData.patientInfo.age === undefined) {
      errors.age = 'Age is required';
    } else if (formData.patientInfo.age <= 0 || formData.patientInfo.age > 150) {
      errors.age = 'Please enter a valid age';
    }
    if (!formData.patientInfo.gender) {
      errors.gender = 'Gender is required';
    }
    if (!formData.patientInfo.postalCode.trim()) {
      errors.postalCode = 'Postal code is required';
    }
    return errors;
  };

  const errors = getErrors();
  const hasErrors = Object.keys(errors).length > 0;

  const markTouched = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const showError = (field: keyof FormErrors) => touched[field] && errors[field];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: UploadedFile[] = Array.from(selectedFiles).map((file) => ({
      file,
      id: crypto.randomUUID(),
    }));
    setFiles((prev) => [...prev, ...newFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const result = await api.cases.create({
        title: data.title,
        condition: data.condition,
        patientInfo: {
          age: data.patientInfo.age,
          gender: data.patientInfo.gender || undefined,
          canton: data.patientInfo.canton || undefined,
          postalCode: data.patientInfo.postalCode || undefined,
        },
      });

      const caseId = (result.data as { id: string }).id;

      if (files.length > 0) {
        await api.cases.uploadDocuments(caseId, files.map((f) => f.file));
      }

      return caseId;
    },
    onSuccess: (caseId) => {
      toast.success('Case created', 'Your case has been created successfully.');
      navigate(`/cases/${caseId}`);
    },
    onError: (error) => {
      toast.error('Failed to create case', error instanceof Error ? error.message : 'An error occurred');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mark all fields as touched to show validation errors
    setTouched({
      title: true,
      condition: true,
      age: true,
      gender: true,
      postalCode: true,
    });
    if (!hasErrors) {
      createMutation.mutate(formData);
    }
  };

  // Helper component for inline errors
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return (
      <p className="flex items-center gap-1 text-xs text-red-600 mt-1" role="alert">
        <AlertCircle className="h-3 w-3" />
        {error}
      </p>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        breadcrumbs={[
          { label: 'Cases', href: '/cases' },
          { label: 'New Case', icon: Plus },
        ]}
        title="New Case"
        description="Enter patient details to start analysis"
        actions={
          <div className="flex gap-2">
            <button type="button" onClick={() => navigate('/cases')} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              form="case-form"
              disabled={createMutation.isPending}
              className="btn-primary"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </>
              )}
            </button>
          </div>
        }
      />

      {/* Validation Summary - shown when form has errors and user tries to submit */}
      {hasErrors && Object.keys(touched).length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 mb-4" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
              <ul className="text-xs text-red-700 mt-1 list-disc list-inside">
                {Object.values(errors).map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form id="case-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main content - Case & Condition */}
          <div className="lg:col-span-2 space-y-4">
            {/* Case Title */}
            <div className="card">
              <label htmlFor="title" className="label">
                Case Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                onBlur={() => markTouched('title')}
                placeholder="e.g., Chronic back pain consultation"
                className={`input mt-1 ${showError('title') ? 'input-error' : ''}`}
                aria-invalid={!!showError('title')}
                aria-describedby={showError('title') ? 'title-error' : undefined}
              />
              {showError('title') && <FieldError error={errors.title} />}
            </div>

            {/* Medical Condition */}
            <div className="card">
              <div className="flex items-center justify-between">
                <label htmlFor="condition" className="label">
                  Medical Condition / Request <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-400">
                  {formData.condition.length} characters
                </span>
              </div>
              <textarea
                id="condition"
                rows={8}
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                onBlur={() => markTouched('condition')}
                placeholder="Describe the patient's condition, symptoms, medical history, and what they are looking for in a care provider..."
                className={`input mt-1 resize-none ${showError('condition') ? 'input-error' : ''}`}
                aria-invalid={!!showError('condition')}
                aria-describedby={showError('condition') ? 'condition-error' : 'condition-help'}
              />
              {showError('condition') ? (
                <FieldError error={errors.condition} />
              ) : (
                <p id="condition-help" className="text-xs text-gray-500 mt-1.5">
                  Provide detailed information for better matching results (minimum 10 characters)
                </p>
              )}
            </div>

            {/* Documents */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="label">Documents</span>
                <span className="text-xs text-gray-400">Optional</span>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                tabIndex={0}
                role="button"
                aria-label="Upload documents"
                className="border border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Upload className="mx-auto h-6 w-6 text-gray-400" />
                <p className="mt-1 text-xs text-gray-600">Click to upload medical records, lab results</p>
                <p className="text-xs text-gray-400 mt-0.5">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  className="hidden"
                  aria-hidden="true"
                />
              </div>

              {files.length > 0 && (
                <div className="mt-2 space-y-1" role="list" aria-label="Uploaded files">
                  {files.map((uploadedFile) => (
                    <div
                      key={uploadedFile.id}
                      className="flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5"
                      role="listitem"
                    >
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                      <span className="text-xs text-gray-700 truncate flex-1">
                        {uploadedFile.file.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatFileSize(uploadedFile.file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(uploadedFile.id)}
                        className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label={`Remove ${uploadedFile.file.name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Patient Info */}
          <div className="space-y-4">
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-900">Patient Details</span>
                <span className="text-xs text-red-500">* Required</span>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="age" className="label text-xs">
                      Age <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="age"
                      min={0}
                      max={150}
                      value={formData.patientInfo.age ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          patientInfo: {
                            ...formData.patientInfo,
                            age: e.target.value ? parseInt(e.target.value, 10) : undefined,
                          },
                        })
                      }
                      onBlur={() => markTouched('age')}
                      placeholder="e.g., 45"
                      className={`input mt-0.5 ${showError('age') ? 'input-error' : ''}`}
                      aria-invalid={!!showError('age')}
                    />
                    {showError('age') && <FieldError error={errors.age} />}
                  </div>

                  <div>
                    <label htmlFor="gender" className="label text-xs">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="gender"
                      value={formData.patientInfo.gender}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          patientInfo: {
                            ...formData.patientInfo,
                            gender: e.target.value as '' | 'male' | 'female' | 'other',
                          },
                        })
                      }
                      onBlur={() => markTouched('gender')}
                      className={`input mt-0.5 ${showError('gender') ? 'input-error' : ''}`}
                      aria-invalid={!!showError('gender')}
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {showError('gender') && <FieldError error={errors.gender} />}
                  </div>
                </div>

                <div>
                  <PostalCodeAutocomplete
                    value={formData.patientInfo.postalCode}
                    onChange={(plz, canton) =>
                      setFormData({
                        ...formData,
                        patientInfo: { ...formData.patientInfo, postalCode: plz, canton },
                      })
                    }
                    onBlur={() => markTouched('postalCode')}
                    label="Postal Code"
                    required
                    error={showError('postalCode') ? errors.postalCode : undefined}
                  />
                </div>

                <div>
                  <label htmlFor="canton" className="label text-xs">Canton</label>
                  <input
                    type="text"
                    id="canton"
                    value={
                      formData.patientInfo.canton
                        ? SWISS_CANTONS.find((c) => c.code === formData.patientInfo.canton)?.name ||
                          formData.patientInfo.canton
                        : ''
                    }
                    readOnly
                    className="input mt-0.5 bg-gray-50 text-gray-500"
                    placeholder="Auto-filled from postal code"
                    aria-describedby="canton-help"
                  />
                  <p id="canton-help" className="text-xs text-gray-400 mt-0.5">
                    Automatically determined from postal code
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
              <p className="text-xs text-primary-800 font-medium mb-1">What happens next?</p>
              <p className="text-xs text-primary-700">
                AI will analyze the condition and generate tailored questions to match the best
                care providers.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
